// The flows' calls to the API: the same /widget/… endpoints v1's booking
// pages use (the booking's id is what lets the customer in; pay-next-due is
// the one that also needs them signed in).

/** A failed call: its status and the API's own message when it gave one. */
export class BookingApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /** The API's customer-facing wording, when it gave one. */
    readonly uxMessage?: string,
    /** The API's error type (`app_error_type`, e.g.
     * "PROPOSAL_ACCEPT_IN_PROGRESS"), when it gave one. */
    readonly appErrorType?: string,
  ) {
    super(message);
    this.name = "BookingApiError";
  }
}

/** The API's message for a failure (its customer-facing wording first),
 * else `fallback`. */
export function messageOf(error: unknown, fallback: string): string {
  if (!(error instanceof BookingApiError)) return fallback;
  return error.uxMessage || error.message || fallback;
}

/** JSON from the API; a non-2xx answer throws a BookingApiError with the
 * body's `message` (or `error`), `code` (text or a number) and
 * `ux_message`. Never logs the request: it may
 * carry the customer's session. */
export async function bookingJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const fields = (body ?? {}) as Record<string, unknown>;
    const field = (key: string) => (typeof fields[key] === "string" ? (fields[key] as string) : undefined);
    const message = field("message") ?? field("error") ?? "";
    const code = typeof fields.code === "number" ? String(fields.code) : field("code");
    const uxMessage = field("ux_message");
    throw new BookingApiError(res.status, message, code, uxMessage, field("app_error_type"));
  }
  return body as T;
}

/** A POST/PUT with a JSON body. */
export const sendJson = <T>(url: string, method: "POST" | "PUT" | "DELETE", body: unknown = {}, init?: RequestInit) =>
  bookingJson<T>(url, { ...init, method, body: JSON.stringify(body) });
